import { useState, useEffect } from "react";
import styles from "./list.module.css";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, TableFooter, Dialog, Snackbar, Alert, Switch } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../components/TableComponent";
import TablePaginationComponent from "../../components/TablePagination";
import { Add, Close } from "@mui/icons-material";
import axios from "axios";
import DeletePopup from "../../components/DeletePopup";
import Loader from "../../components/Loader";
import ProductListLoader from "./ProductListLoader";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Edit, Search, Trash2 } from "react-feather";
import PageHeader from "../../components/PageHeader";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import AddDeliveryProductCategoryForm from "../../components/popups/AddDeliveryProductCategoryForm";

const ProductCategoryList = () => {
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [filterVars, setFilterVars] = useState({ searchKey: "" });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [deleteModalOpen, setDeleteModalOpen] = useState();
  const [deleteId, setDeleteId] = useState(0);
  const [editData, setEditData] = useState(null);
  const auth = authData();
  const API_TOKEN = auth?.api_token;

  const { websiteData } = useWebsite();
  useSiteBranding("Delivery item category list", websiteData);

  const canUpdate = auth?.user_type === 2 || auth?.permissions?.indexOf('items:update') > -1;

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchProducts = () => {
      setContentLoading(true);
      axios.get(process.env.REACT_APP_API_URL + "v1/delivery-products", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setContentLoading(false);

        let categoryItems = response.data.data?.filter(i => i?.parent_id === 0);

        setAllData(categoryItems);
      }).catch((error) => {
        setContentLoading(false);
      });
    };

    fetchProducts();
  }, [API_TOKEN]);

  useEffect(() => {
    let filterredTempData = allData;

    if (filterVars?.searchKey !== "") {
      let inputVal = filterVars?.searchKey;
      inputVal = inputVal.toLowerCase();
      filterredTempData = filterredTempData.filter((item) => {
        return item.title.toLowerCase().includes(inputVal);
      });
    }

    setFilteredData(filterredTempData);
  }, [allData, filterVars]);

  const deleteConfirm = () => {
    setLoading(true);
    setDeleteModalOpen(false);
    axios.delete(process.env.REACT_APP_API_URL + "v1/delivery-products/" + deleteId, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("success");
        setAllData((prev) => {
          let temp = prev.filter((i) => i.id !== deleteId);
          return temp;
        });
        setDeleteId(0);
      }
      if (response.data.status === "ERROR") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("error");
      }
    }).catch((error) => {
      setLoading(false);
      if (error && error.response && error.response.data && error.response.data.messages) {
        setSnackbarOpen(true);
        setSnackbarMessage(error.response.data.messages);
        setSnackbarType("error");
      }
    });
  };

  const statusChange = (e, item) => {
    let status = 0;
    if (e.target.checked) {
      status = 1;
    }
    setLoading(true);

    axios.put(process.env.REACT_APP_API_URL + "v1/delivery-products/" + item?.id, { status: status }, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);

      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("success");

        setAllData((prev) => {
          let temp = prev.map((i) => {
            if (i.id === item?.id) {
              return { ...item, status: status };
            }
            return i;
          });
          return temp;
        });
      }

      if (response.data.status === "ERROR") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("error");
      }
    }).catch((error) => {
      setLoading(false);
      if (error && error.response && error.response.data && error.response.data.messages) {
        setSnackbarOpen(true);
        setSnackbarMessage(error.response.data.messages);
        setSnackbarType("error");
      }
    });
  };

  // Categories without items are hidden from the booking forms
  const itemCount = (item) => item?.no_of_child || 0;

  const renderActions = (item) => (<>
    <Button onClick={(e) => { setFormOpen(true); setEditData(item); }} className={`${styles.TableActionBU}`} aria-label="Edit item"><Edit /></Button>
    <Button onClick={(e) => { setDeleteModalOpen(true); setDeleteId(item?.id); }} className={`${styles.TableActionBU}`} aria-label="Delete item"><Trash2 /></Button>
  </>);

  const pageSlice = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Delivery item categories" trail={[{ label: "List" }]} />
      <div className={`${styles.MainCard}`}>

        {contentLoading && <ProductListLoader />}

        {!contentLoading && (<>
          <div className={`${styles.TableFilterHead}`}>
            <div className={styles.SearchWrap}>
              <Search className={styles.SearchIcon} />
              <TextField
                variant="standard"
                type="text"
                placeholder="Search delivery item categories"
                value={filterVars?.searchKey}
                onChange={(e) => setFilterVars((prev) => ({ ...prev, searchKey: e.target.value }))}
                className={styles.SearchInput}
                InputProps={{ disableUnderline: true }}
                fullWidth
              />
              {filterVars?.searchKey !== "" && (
                <Button type="button" className={styles.ClearBU} onClick={(e) => setFilterVars((prev) => ({ ...prev, searchKey: "" }))}>
                  <Close />
                </Button>
              )}
            </div>
            {(auth?.user_type === 2 || auth?.permissions?.indexOf('items:add') > -1) && <div className={`${styles.TableFilterBUSec}`}>
              <Button className={`${styles.TableAddBU}`} onClick={(e) => { setFormOpen(true); setEditData(null); }}><Add /> Item Category</Button>
            </div>}
          </div>

          {/* Desktop table */}
          <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="Delivery product list table">
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell>Title</StyledMainTableCell>
                    <StyledMainTableCell>Items</StyledMainTableCell>
                    <StyledMainTableCell>Status</StyledMainTableCell>
                    {canUpdate && <StyledMainTableCell></StyledMainTableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pageSlice.map((item, index) => {
                    return (<StyledMainTableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row" style={{ width: 200 }}>{item?.title}</TableCell>
                      <TableCell component="th" scope="row" style={{ width: 120 }}>{itemCount(item)}</TableCell>
                      <TableCell component="th" scope="row" style={{ width: 120 }}>
                        <Switch
                          checked={item?.status}
                          onChange={(e) => statusChange(e, item)}
                          inputProps={{ "aria-label": "controlled" }}
                          disabled={!canUpdate}
                          className={styles.CustomSwitch}
                        />
                      </TableCell>
                      {canUpdate && <TableCell component="th" scope="row" align="right" style={{ width: 100 }}>
                        {renderActions(item)}
                      </TableCell>}
                    </StyledMainTableRow>);
                  })}
                  {filteredData.length === 0 && (<TableRow>
                    <TableCell colSpan={6} align="center">No data found</TableCell>
                  </TableRow>)}
                </TableBody>

                {filteredData.length > 0 && (<TableFooter>
                  <TableRow>
                    <TablePaginationComponent
                      count={filteredData.length}
                      colSpan={6}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={
                        paginationChangeRowsPerPage
                      }
                    />
                  </TableRow>
                </TableFooter>)}
              </Table>
            </TableContainer>
          </div>

          {/* Mobile stacked cards — shown instead of the table below 768px */}
          <div className={`${styles.MobileCardList}`}>
            {pageSlice.map((item, index) => (
              <div className={`${styles.CustomerCard}`} key={index}>
                <div className={`${styles.CustomerCardHead}`}>
                  <div>
                    <p className={`${styles.CustomerCardName}`}>{item?.title}</p>
                  </div>
                  <Switch checked={item?.status} onChange={(e) => statusChange(e, item)} inputProps={{ "aria-label": "controlled" }} disabled={!canUpdate} />
                </div>
                <div className={`${styles.CustomerCardBody}`}>
                  <p><span>Items</span>{itemCount(item)}</p>
                </div>
                {canUpdate && <div className={`${styles.CustomerCardActions}`}>
                  {renderActions(item)}
                </div>}
              </div>
            ))}
            {filteredData.length === 0 && <div className={`${styles.NoDataCard}`}>No data found</div>}
            {filteredData.length > 0 && (
              <TablePaginationComponent
                count={filteredData.length}
                colSpan={6}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={paginationChangePage}
                handleChangeRowsPerPage={paginationChangeRowsPerPage}
              />
            )}
          </div>
        </>)}
      </div>
    </div>

    <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AddDeliveryProductCategoryForm setFormOpen={setFormOpen.bind(this)} setAllData={setAllData.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={editData} />
    </Dialog>

    <DeletePopup open={deleteModalOpen} setOpen={setDeleteModalOpen.bind(this)} confirmAction={deleteConfirm.bind(this)} title="Delete Delivery Product?" message="Are you sure you want to delete this item? You can't undo this action." />

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(ProductCategoryList);